import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

export type ModuleType = 'admin' | 'send-customer';

@Injectable({
    providedIn: 'root',
})
export class ModuleContextService {
    private readonly moduleContext = signal<ModuleType | null>(null);
    private readonly router = inject(Router);

    readonly currentModule = this.moduleContext.asReadonly();

    /**
     * Set the current module context based on current URL
     */
    initializeModuleContext(): void {
        const url = this.router.url;

        if (url.startsWith('/admin')) {
            this.moduleContext.set('admin');
        } else if (url.startsWith('/sendtestapp')) {
            this.moduleContext.set('send-customer');
        }
    }

    /**
     * Get the current module context
     */
    getModuleContext(): ModuleType | null {
        return this.moduleContext();
    }

    /**
     * Set module context explicitly
     */
    setModuleContext(module: ModuleType): void {
        this.moduleContext.set(module);
    }

    /**
     * Get login route based on module context
     */
    getLoginRoute(): string {
        const module = this.moduleContext();

        if (module === 'admin') {
            return '/admin/login';
        } else if (module === 'send-customer') {
            return '/sendtestapp/login';
        }

        return '/sendtestapp/login'; // default
    }

    /**
     * Get dashboard route based on module context
     */
    getDashboardRoute(): string {
        const module = this.moduleContext();

        if (module === 'admin') {
            return '/admin/main/dashboard';
        } else if (module === 'send-customer') {
            return '/sendtestapp/choose-flow';
        }

        return '/sendtestapp/choose-flow'; // default
    }

    /**
     * Get module display name
     */
    getModuleDisplayName(): string {
        const module = this.moduleContext();

        if (module === 'admin') {
            return 'Admin Portal';
        } else if (module === 'send-customer') {
            return 'Customer Portal';
        }

        return 'Portal';
    }

    /**
     * Check if current module is admin
     */
    isAdminModule(): boolean {
        return this.moduleContext() === 'admin';
    }

    /**
     * Check if current module is send-customer
     */
    isSendCustomerModule(): boolean {
        return this.moduleContext() === 'send-customer';
    }
}
